const UNKNOWN_FLAG = "🗺️";

const flagMap: Record<string, string> = {
  // Regions
  Global: "🌍",
  Africa: "🌍",
  Americas: "🌎",
  Asia: "🌏",
  Europe: "🇪🇺",
  Oceania: "🌊",

  // Europe
  Albania: "🇦🇱",
  Andorra: "🇦🇩",
  Austria: "🇦🇹",
  Belgium: "🇧🇪",
  Bulgaria: "🇧🇬",
  Croatia: "🇭🇷",
  "Czech Republic": "🇨🇿",
  Czechia: "🇨🇿",
  Denmark: "🇩🇰",
  Estonia: "🇪🇪",
  Finland: "🇫🇮",
  France: "🇫🇷",
  Germany: "🇩🇪",
  Greece: "🇬🇷",
  Greenland: "🇬🇱",
  Hungary: "🇭🇺",
  Iceland: "🇮🇸",
  Ireland: "🇮🇪",
  Italy: "🇮🇹",
  Latvia: "🇱🇻",
  Lithuania: "🇱🇹",
  Luxembourg: "🇱🇺",
  Malta: "🇲🇹",
  Moldova: "🇲🇩",
  Monaco: "🇲🇨",
  Montenegro: "🇲🇪",
  Netherlands: "🇳🇱",
  "North Macedonia": "🇲🇰",
  Norway: "🇳🇴",
  Poland: "🇵🇱",
  Portugal: "🇵🇹",
  Romania: "🇷🇴",
  Russia: "🇷🇺",
  Serbia: "🇷🇸",
  Slovakia: "🇸🇰",
  Slovenia: "🇸🇮",
  Spain: "🇪🇸",
  Sweden: "🇸🇪",
  Switzerland: "🇨🇭",
  Ukraine: "🇺🇦",
  "United Kingdom": "🇬🇧",

  // North America
  Canada: "🇨🇦",
  "United States": "🇺🇸",
  USA: "🇺🇸",
  Mexico: "🇲🇽",
  Bermuda: "🇧🇲",
  "Faroe Islands": "🇫🇴",
  "Isle of Man": "🇮🇲",

  // Central America & Caribbean
  "Costa Rica": "🇨🇷",
  "Dominican Republic": "🇩🇴",
  Guatemala: "🇬🇹",
  Panama: "🇵🇦",
  "Puerto Rico": "🇵🇷",
  "U.S. Virgin Islands": "🇻🇮",

  // South America
  Argentina: "🇦🇷",
  Bolivia: "🇧🇴",
  Brazil: "🇧🇷",
  Chile: "🇨🇱",
  Colombia: "🇨🇴",
  Ecuador: "🇪🇨",
  Peru: "🇵🇪",
  Uruguay: "🇺🇾",

  // Asia
  Bangladesh: "🇧🇩",
  Bhutan: "🇧🇹",
  Cambodia: "🇰🇭",
  China: "🇨🇳",
  "Christmas Island": "🇨🇽",
  "Hong Kong": "🇭🇰",
  India: "🇮🇳",
  Indonesia: "🇮🇩",
  Japan: "🇯🇵",
  Kyrgyzstan: "🇰🇬",
  Laos: "🇱🇦",
  Malaysia: "🇲🇾",
  Mongolia: "🇲🇳",
  Pakistan: "🇵🇰",
  Philippines: "🇵🇭",
  Singapore: "🇸🇬",
  "South Korea": "🇰🇷",
  "Sri Lanka": "🇱🇰",
  Taiwan: "🇹🇼",
  Thailand: "🇹🇭",
  Vietnam: "🇻🇳",

  // Middle East
  Israel: "🇮🇱",
  Jordan: "🇯🇴",
  Palestine: "🇵🇸",
  Qatar: "🇶🇦",
  Tunisia: "🇹🇳",
  Turkey: "🇹🇷",
  "United Arab Emirates": "🇦🇪",

  // Oceania
  "American Samoa": "🇦🇸",
  Australia: "🇦🇺",
  Guam: "🇬🇺",
  "New Zealand": "🇳🇿",
  "Northern Mariana Islands": "🇲🇵",
  "U.S. Minor Outlying Islands": "🇺🇲",

  // Africa
  Botswana: "🇧🇼",
  Eswatini: "🇸🇿",
  Ghana: "🇬🇭",
  Kenya: "🇰🇪",
  Lesotho: "🇱🇸",
  Madagascar: "🇲🇬",
  Nigeria: "🇳🇬",
  Rwanda: "🇷🇼",
  Senegal: "🇸🇳",
  "South Africa": "🇿🇦",
  Uganda: "🇺🇬",
};

export function getCountryFlag(geography: string): string {
  if (!geography) {
    return UNKNOWN_FLAG;
  }

  const normalized = geography.trim();
  if (!normalized) {
    return UNKNOWN_FLAG;
  }

  return flagMap[normalized] ?? UNKNOWN_FLAG;
}

export function getFlagFromLabel(label: string): string {
  if (!label) {
    return UNKNOWN_FLAG;
  }

  const segments = label
    .split("·")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  for (const segment of segments) {
    const flag = getCountryFlag(segment);
    if (flag !== UNKNOWN_FLAG) {
      return flag;
    }
  }

  if (segments.length > 0 && segments[0]) {
    return getCountryFlag(segments[0]);
  }

  return UNKNOWN_FLAG;
}

export function getFlagsForRegions(
  regions: string[],
  fallbackLabel?: string,
  limit = 3,
): string {
  if (!Array.isArray(regions) || regions.length === 0) {
    return fallbackLabel ? getFlagFromLabel(fallbackLabel) : UNKNOWN_FLAG;
  }

  const uniqueFlags: string[] = [];

  regions
    .map((region) => region.trim())
    .filter((region) => region.length > 0)
    .forEach((region) => {
      const flag = getCountryFlag(region);
      if (flag !== UNKNOWN_FLAG && !uniqueFlags.includes(flag)) {
        uniqueFlags.push(flag);
      }
    });

  if (uniqueFlags.length > 0) {
    return uniqueFlags.slice(0, limit).join(" ");
  }

  return fallbackLabel ? getFlagFromLabel(fallbackLabel) : UNKNOWN_FLAG;
}
